/* ==========================================================================
   Parasitic Draw Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-pdc]');
  if (!root) return;

  /* Typical accepted draw for a modern vehicle once all modules have slept. */
  var NORMAL_MAX_MA = 50;
  var OLDER_MAX_MA  = 25;
  /* Modules can take 20 to 45 minutes to power down after the doors are shut. */
  var SLEEP_MINUTES = 45;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    var mA = input.unit === 'A' ? input.draw * 1000 : input.draw;
    if (!isFinite(mA) || mA < 0) {
      return { ok: false, errors: ['Enter the current you measured.'] };
    }
    if (!isFinite(input.capacity) || input.capacity <= 0) {
      return { ok: false, errors: ['Enter the battery capacity in amp hours.'] };
    }

    var usableFrac = 1 - (parseFloat(input.startLevel) / 100);
    var usableAh = input.capacity * usableFrac;
    var amps = mA / 1000;

    var hours = amps > 0 ? usableAh / amps : Infinity;
    var days = hours / 24;

    var parked = isFinite(input.days) && input.days > 0 ? input.days : 14;
    var consumed = amps * 24 * parked;
    var pctUsed = (consumed / input.capacity) * 100;

    var status, warnings = [];
    if (mA <= OLDER_MAX_MA) {
      status = { key: 'good', label: num(mA, 0) + ' mA, normal',
                 note: 'Well inside spec. This is not what is flattening your battery.' };
    } else if (mA <= NORMAL_MAX_MA) {
      status = { key: 'good', label: num(mA, 0) + ' mA, normal',
                 note: 'Acceptable for a modern vehicle with keyless entry and telematics.' };
    } else if (mA <= 100) {
      status = { key: 'monitor', label: num(mA, 0) + ' mA, slightly high',
                 note: 'Above the usual 50mA ceiling. Worth chasing if the vehicle sits for long periods.' };
    } else if (mA <= 500) {
      status = { key: 'soon', label: num(mA, 0) + ' mA, too high',
                 note: 'Something is staying awake. Pull fuses one at a time and watch for the drop.' };
    } else {
      status = { key: 'now', label: num(mA, 0) + ' mA, fault',
                 note: 'This will flatten a healthy battery in days. Find it before replacing anything.' };
    }

    if (mA > NORMAL_MAX_MA && mA < 200) {
      warnings.push('Check the vehicle has actually gone to sleep. Modules can take 45 minutes to power down, and opening a door restarts the clock.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Days until it will not start', value: isFinite(days) ? num(days, 1) + ' days' : 'Indefinite',
          sub: 'Using ' + num(usableAh, 1) + 'Ah of a ' + num(input.capacity, 0) + 'Ah battery before it drops below your reserve.' },
        { label: 'After ' + num(parked, 0) + ' days parked', value: num(Math.min(100, pctUsed), 0) + '% used',
          sub: num(consumed, 1) + 'Ah drawn out of the battery.' }
      ],
      rows: [
        { head: 'Measurement' },
        { label: 'Draw', value: num(mA, 1) + ' mA  (' + num(amps, 3) + ' A)' },
        { label: 'Normal ceiling', value: NORMAL_MAX_MA + ' mA' },
        { label: 'Per day', value: num(amps * 24, 2) + ' Ah' },
        { head: 'Battery' },
        { label: 'Capacity', value: num(input.capacity, 0) + ' Ah' },
        { label: 'Usable before no-start', value: num(usableAh, 1) + ' Ah' },
        { label: 'Time to that point', value: isFinite(days) ? num(days, 1) + ' days' : 'Indefinite' },
        { head: 'Finding it' },
        { label: 'Let it sleep', value: SLEEP_MINUTES + ' minutes' },
        { label: 'Method', value: 'Pull fuses one at a time' },
        { label: 'Watch for', value: 'A sudden drop in the reading' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "draw",
          "key": "d",
          "type": "number"
      },
      {
          "name": "unit",
          "key": "u",
          "type": "select"
      },
      {
          "name": "capacity",
          "key": "c",
          "type": "number"
      },
      {
          "name": "startLevel",
          "key": "s",
          "type": "select"
      },
      {
          "name": "days",
          "key": "dy",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-pdc__figure">'
        + '<div class="pc-pdc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-pdc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-pdc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-pdc__row pc-pdc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-pdc__row">'
        + '<span class="pc-pdc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-pdc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_PDC = { compute: compute };
})();
